import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import styles from './SearchBar.module.css';

/**
 * Debounced storefront search. Typing navigates to the catalog with a `search`
 * query param after a short delay; submitting (Enter) navigates immediately.
 */
export function SearchBar({ initialValue = '' }: { initialValue?: string }) {
  const [value, setValue] = useState(initialValue);
  const navigate = useNavigate();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the field in sync when the URL changes elsewhere (e.g. clearing).
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const go = (next: string) => {
    const trimmed = next.trim();
    navigate(trimmed ? `/catalog?search=${encodeURIComponent(trimmed)}` : '/catalog');
  };

  const onChange = (next: string) => {
    setValue(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => go(next), 350);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (timer.current) clearTimeout(timer.current);
    go(value);
  };

  return (
    <form className={styles.form} role="search" onSubmit={onSubmit}>
      <span className={styles.icon}>
        <Icon name="search" size={18} />
      </span>
      <input
        className={styles.input}
        type="search"
        name="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search seeds, pets, gear…"
        aria-label="Search products"
        autoComplete="off"
      />
      {value && (
        <button
          type="button"
          className={styles.clear}
          onClick={() => {
            setValue('');
            go('');
          }}
          aria-label="Clear search"
        >
          <Icon name="close" size={16} />
        </button>
      )}
    </form>
  );
}
